var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { ObjectTyped } from '@timeflies/common';
import { Howl } from 'howler';
import React from 'react';
var audioContext = new globalThis.AudioContext();
var unlockAudioContext = function () {
    if (audioContext.state !== "suspended") {
        return;
    }
    var events = ["touchstart", "touchend", "mousedown", "keydown"];
    var unlock = function () {
        audioContext.resume().finally(clean);
    };
    var clean = function () { return events.forEach(function (e) { return document.body.removeEventListener(e, unlock); }); };
    events.forEach(function (e) { return document.body.addEventListener(e, unlock, false); });
};
unlockAudioContext();
export var createAudioContext = function (_a) {
    var _b;
    var contextsNamePrefix = _a.contextsNamePrefix, storageAudioVolumeKeyPrefix = _a.storageAudioVolumeKeyPrefix, defaultAudioVolume = _a.defaultAudioVolume, assets = _a.assets, loop = _a.loop;
    var AudioContext = React.createContext(null);
    AudioContext.displayName = contextsNamePrefix + "Context";
    var storageAudioVolumeKey = "options." + storageAudioVolumeKeyPrefix + "-volume";
    var initialAudioVolume = Number((_b = localStorage.getItem(storageAudioVolumeKey)) !== null && _b !== void 0 ? _b : defaultAudioVolume);
    var AudioVolumeContext = React.createContext(initialAudioVolume);
    AudioVolumeContext.displayName = contextsNamePrefix + "VolumeContext";
    var AudioVolumeDispatchContext = React.createContext(undefined);
    AudioVolumeDispatchContext.displayName = contextsNamePrefix + "VolumeDispatchContext";
    var AudioCurrentContext = React.createContext(null);
    AudioCurrentContext.displayName = contextsNamePrefix + "CurrentContext";
    var AudioCurrentDispatchContext = React.createContext(undefined);
    AudioCurrentDispatchContext.displayName = contextsNamePrefix + "CurrentDispatchContext";
    var contextList = [AudioContext, AudioVolumeContext, AudioVolumeDispatchContext, AudioCurrentContext, AudioCurrentDispatchContext];
    var applyAudioVolume = function (audioContext, volume) { return Object.values(audioContext)
        .forEach(function (audioList) { return audioList
        .forEach(function (audio) { return audio.volume(volume); }); }); };
    var enableAudioContext = function () {
        if (![audioContext.state, new globalThis.AudioContext().state].includes('running')) {
            return null;
        }
        var audioContextValue = ObjectTyped.entries(assets)
            .reduce(function (acc, _a) {
            var key = _a[0], audioList = _a[1];
            var audios = audioList.map(function (audioPaths) { return new Howl({
                src: audioPaths,
                html5: true,
                volume: initialAudioVolume,
                loop: loop
            }); });
            acc[key] = audios;
            return acc;
        }, {});
        return audioContextValue;
    };
    var AudioContextProvider = function (_a) {
        var children = _a.children;
        var _b = React.useState(enableAudioContext), audioValue = _b[0], audioDispatch = _b[1];
        var _c = React.useState(initialAudioVolume), audioVolumeValue = _c[0], audioVolumeDispatch = _c[1];
        var _d = React.useReducer(function (prevAudioCurrent, nextAudioCurrent) {
            var _a;
            if (!nextAudioCurrent) {
                return null;
            }
            return __assign(__assign({}, nextAudioCurrent), { history: __assign(__assign({}, prevAudioCurrent === null || prevAudioCurrent === void 0 ? void 0 : prevAudioCurrent.history), (_a = {}, _a[nextAudioCurrent.currentKey] = nextAudioCurrent.index, _a)) });
        }, null), audioCurrentValue = _d[0], audioCurrentDispatch = _d[1];
        React.useEffect(function () {
            audioContext.addEventListener('statechange', function () {
                audioDispatch(enableAudioContext());
            });
        }, []);
        return React.createElement(AudioContext.Provider, { value: audioValue },
            React.createElement(AudioVolumeContext.Provider, { value: audioVolumeValue },
                React.createElement(AudioVolumeDispatchContext.Provider, { value: audioVolumeDispatch },
                    React.createElement(AudioCurrentContext.Provider, { value: audioCurrentValue },
                        React.createElement(AudioCurrentDispatchContext.Provider, { value: audioCurrentDispatch }, children)))));
    };
    var useAudioContext = function () { return React.useContext(AudioContext); };
    var useAudioVolumeContext = function () { return React.useContext(AudioVolumeContext); };
    var useAudioVolumeDispatch = function () {
        var musicContext = useAudioContext();
        var dispatch = React.useContext(AudioVolumeDispatchContext);
        return function (volume) {
            if (musicContext) {
                applyAudioVolume(musicContext, volume);
            }
            localStorage.setItem(storageAudioVolumeKey, volume + '');
            dispatch(volume);
        };
    };
    var useAudioCurrentContext = function () { return React.useContext(AudioCurrentContext); };
    var useAudioCurrentDispatch = function () { return React.useContext(AudioCurrentDispatchContext); };
    var getNextAudio = function (audioContext, currentAudio, audioKey, assets) {
        var _a;
        var prevIndex = (_a = currentAudio === null || currentAudio === void 0 ? void 0 : currentAudio.history[audioKey]) !== null && _a !== void 0 ? _a : -1;
        var nextIndex = (prevIndex + 1) % assets[audioKey].length;
        var nextAudio = audioContext[audioKey][nextIndex];
        return {
            prevIndex: prevIndex,
            nextIndex: nextIndex,
            nextAudio: nextAudio
        };
    };
    return {
        AudioContextProvider: AudioContextProvider,
        contextList: contextList,
        useAudioContext: useAudioContext,
        useAudioVolumeContext: useAudioVolumeContext,
        useAudioVolumeDispatch: useAudioVolumeDispatch,
        useAudioCurrentContext: useAudioCurrentContext,
        useAudioCurrentDispatch: useAudioCurrentDispatch,
        getNextAudio: getNextAudio
    };
};
